import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react'
import { ArrowLeft, ArrowLeftRight, ArrowRight } from 'lucide-react'
import s from './RelationshipTabStrip.module.css'

export interface RelationshipTab {
  /** Stable slot id — `grid-rel-tab-<key>`. */
  key: string
  label: string
  /** Measured count; `null` while unknown (never drawn as zero). */
  count: number | null
  /** The count reached the read's cap — drawn as `N+`. */
  capped?: boolean
  /** Which way the relationship reads from the record in the row. */
  direction: 'out' | 'in' | 'both'
  /** A leading glyph, e.g. the far entity's registered icon. */
  icon?: ReactNode
}

export interface RelationshipTabGroup {
  /** Stable group key — `grid-rel-group-<key>`. */
  key: string
  /** The governed group label ("Commercial"). */
  label: string
  tabs: readonly RelationshipTab[]
}

export interface RelationshipTabStripProps {
  groups: readonly RelationshipTabGroup[]
  selected?: string | null
  onSelect: (key: string) => void
  /** Drawn after the strip — the "Show N empty" affordance, where a host has one. */
  trailing?: ReactNode
  testId?: string
  /** A host's established per-tab test id; defaults to `grid-rel-tab-<key>`. */
  tabTestId?: (key: string) => string
}

const DIRECTION_WORDS: Record<RelationshipTab['direction'], string> = {
  out: 'outgoing', in: 'incoming', both: 'both directions',
}

/**
 * THE RELATIONSHIP TABS above a nested collection (#2375, approved §9).
 *
 * One tab per relationship type, each with its count and its DIRECTION as a
 * drawn 12px arrow — a text arrow at small sizes renders as a dash. Tabs are
 * grouped into CLUSTERS: the governed group label on top with a bracket (a
 * hairline ending in a downward tick) over exactly that group's tabs, 20px
 * between clusters, no vertical dividers. The group order and membership are
 * the host's governed data (`core.related_order`); nothing here names a group.
 *
 * The active tab joins the nested card below it (the card's top-left corner is
 * square, CollectionWorkspace `[data-tabbed]`). The strip scrolls sideways when
 * it overflows — it never wraps and never collapses into a dropdown.
 *
 * WAI-ARIA tabs: one `tablist` that OWNS its tabs directly, a roving tab stop,
 * arrows/Home/End move and select across every cluster. A cluster is visual
 * only (`role="none"`): ARIA 1.2 lets a tablist own tabs and nothing else, and
 * clusters as `role="group"` failed axe's aria-required-children/-parent
 * (critical) the moment the brand-text sweep opened a band. Each tab names its
 * cluster through `aria-describedby` on the visible label. The direction is
 * spoken in words ("outgoing"), never as the metamodel's `out` key.
 */
export function RelationshipTabStrip({
  groups, selected, onSelect, trailing, testId = 'grid-rel-tabs', tabTestId = (key) => `grid-rel-tab-${key}`,
}: RelationshipTabStripProps) {
  const list = useRef<HTMLDivElement>(null)
  const idBase = useId()
  const flat = groups.flatMap((group) => group.tabs)
  const active = flat.some((tab) => tab.key === selected) ? selected : flat[0]?.key
  const move = (event: KeyboardEvent<HTMLButtonElement>, key: string) => {
    const current = flat.findIndex((tab) => tab.key === key)
    let next = current
    if (event.key === 'ArrowRight') next = (current + 1) % flat.length
    else if (event.key === 'ArrowLeft') next = (current - 1 + flat.length) % flat.length
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = flat.length - 1
    else return
    event.preventDefault()
    const target = flat[next]
    if (!target) return
    onSelect(target.key)
    list.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus()
  }
  const showLabels = groups.length > 1 || groups.some((group) => group.label)
  return (
    <div className={s.root} data-testid={testId}>
      <div ref={list} className={s.strip} role="tablist" aria-label="Relationships">
        {groups.map((group) => group.tabs.length === 0 ? null : (
          <div key={group.key} className={s.cluster} role="none"
            data-testid={`grid-rel-group-${group.key}`}>
            {showLabels && (
              <span className={s.bracket} aria-hidden="true">
                <span className={s.groupLabel} id={`${idBase}-group-${group.key}`}>{group.label}</span>
              </span>
            )}
            <span className={s.tabs} role="none">
              {group.tabs.map((tab) => {
                const isActive = tab.key === active
                const Arrow = tab.direction === 'out' ? ArrowRight : tab.direction === 'in' ? ArrowLeft : ArrowLeftRight
                const count = tab.count == null ? null : tab.capped ? `${tab.count}+` : String(tab.count)
                const words = DIRECTION_WORDS[tab.direction]
                return (
                  <button key={tab.key} role="tab" aria-selected={isActive} type="button" className={s.tab}
                    tabIndex={isActive ? 0 : -1}
                    aria-describedby={showLabels && group.label ? `${idBase}-group-${group.key}` : undefined}
                    data-rel-tab={tab.key} data-testid={tabTestId(tab.key)}
                    title={`${tab.label} · ${words}`}
                    onKeyDown={(event) => move(event, tab.key)}
                    onClick={() => onSelect(tab.key)}>
                    {tab.icon}
                    <span className={s.label}>{tab.label}</span>
                    {count != null && <span className={s.count}>{count}</span>}
                    <Arrow size={12} strokeWidth={2.25} className={s.direction} role="img" aria-label={words} />
                  </button>
                )
              })}
            </span>
          </div>
        ))}
      </div>
      {trailing}
    </div>
  )
}
